/**
 * The pure helpers of the tab "Email to order" (`GET /sales-truth/trail`, 4 Nov 2026): the
 * normalised item, the alignment of the parts across the columns "Extraction",
 * "Quotation" and "Sales order", the filter chips, the tone of a score and the CSV export.
 * No React and no request, so `tests/learn-from-sales-trail.test.mjs` checks them directly.
 *
 * The alignment: one row per quoted line, with the I2Q line whose item is the item I2Q
 * picked for it or the quoted item, and the sales order line of the same item; then the
 * I2Q lines that no quoted line took; then the lines added on the sales order.
 *
 * Version 2 (4 Nov 2026): the touch points split at the quote sent (`splitTouches`), the
 * reasons of a string as a filter (`TRAIL_REASONS`) and the top reasons of an area
 * (`topReasons`: items first, then the customer, then the quantity).
 */

import type {
  OrderLine,
  TrailAccount,
  TrailDraftLine,
  TrailQuotedLine,
  TrailString,
  TrailTagCounts,
  TrailTouch,
} from "@/lib/api";

import { TRAIL_LABELS, TRAIL_REASON_LABELS, TRAIL_TAG_LABELS } from "./trail-labels";

export type TrailTag = keyof typeof TRAIL_TAG_LABELS;
/** The tags in the order of the page (`sales_truth.trail.TAGS`). */
export const TRAIL_TAGS = Object.keys(TRAIL_TAG_LABELS) as TrailTag[];
export type TrailReason = keyof typeof TRAIL_REASON_LABELS;
/** The reasons in the order of the page: items, then the customer, then the quantity. */
export const TRAIL_REASONS = Object.keys(TRAIL_REASON_LABELS) as TrailReason[];
/** The chips of the filter: every tag, "Not fully right" and every reason. */
export const TRAIL_FILTERS = [...TRAIL_TAGS, "not_right", ...TRAIL_REASONS] as const;
export type TrailFilter = (typeof TRAIL_FILTERS)[number];

/** One row of the table "Top reasons". */
export interface TrailReasonRow {
  reason: TrailReason;
  strings: number;
}

/** The touch points of a string up to the quote sent and after it. */
export interface TrailTouchSplit {
  before: TrailTouch[];
  after: TrailTouch[];
}

/** The bands of a score: green from 90 %, amber from 70 %, red below. */
export const GOOD_SHARE = 0.9;
export const FAIR_SHARE = 0.7;

export type TrailTone = "success" | "warning" | "danger" | "neutral";

/** The order cell of an aligned row: an ordered quoted line or a line added on the order. */
export interface TrailOrderCell {
  item: string;
  qty: number | null;
  added: boolean;
}

/** One row of the parts, the same in the columns 2 to 4. */
export interface TrailPartRow {
  key: string;
  draft: TrailDraftLine | null;
  quoted: TrailQuotedLine | null;
  order: TrailOrderCell | null;
}

/** An item for comparing: upper case, without spaces and `-_./` (as the server). */
export function normItem(text: string | null | undefined): string {
  return (text ?? "").replace(/[\s\-_./]+/g, "").toUpperCase();
}

/** The parts of a string, aligned row by row (see the module comment). */
export function alignParts(string: TrailString): TrailPartRow[] {
  const drafts = string.extraction ?? [];
  const used = new Set<number>();
  const rows: TrailPartRow[] = [];
  for (const quoted of string.quoted ?? []) {
    const wanted = [normItem(quoted.i2q_item), normItem(quoted.sales_item)].filter(Boolean);
    let at = -1;
    for (const code of wanted) {
      at = drafts.findIndex((line, index) => !used.has(index) && normItem(line.item) === code);
      if (at >= 0) {
        break;
      }
    }
    if (at >= 0) {
      used.add(at);
    }
    rows.push({
      key: `q:${quoted.key}`,
      draft: at >= 0 ? (drafts[at] ?? null) : null,
      quoted,
      order: quoted.ordered
        ? {
            item: quoted.sales_item,
            qty: quoted.ordered_qty ?? quoted.qty ?? null,
            added: false,
          }
        : null,
    });
  }
  drafts.forEach((draft, index) => {
    if (!used.has(index)) {
      rows.push({ key: `d:${draft.key}`, draft, quoted: null, order: null });
    }
  });
  (string.order_only ?? []).forEach((line: OrderLine, index) => {
    rows.push({
      key: `o:${index}:${normItem(line.item)}`,
      draft: null,
      quoted: null,
      order: { item: line.item, qty: line.qty ?? null, added: true },
    });
  });
  return rows;
}

/** The strings of one area (`undefined`: every area) that fit the chip. */
export function filterStrings(
  strings: readonly TrailString[],
  area: string | undefined,
  filter: TrailFilter | undefined,
): TrailString[] {
  return strings.filter((string) => {
    if (area && string.area !== area) {
      return false;
    }
    if (!filter) {
      return true;
    }
    if (filter === "not_right") {
      return !string.fully_right;
    }
    if ((TRAIL_REASONS as readonly string[]).includes(filter)) {
      return (string.reasons ?? []).includes(filter);
    }
    return (string.tags ?? []).includes(filter as TrailTag);
  });
}

/** The tone of a score: green from 90 %, amber from 70 %, red below; none without one. */
export function scoreTone(share: number | null | undefined): TrailTone {
  if (typeof share !== "number" || !Number.isFinite(share)) {
    return "neutral";
  }
  if (share >= GOOD_SHARE) {
    return "success";
  }
  return share >= FAIR_SHARE ? "warning" : "danger";
}

/** The reason that misses the most strings, `null` when no string misses. */
export function biggestMiss(tags: TrailTagCounts | null | undefined): TrailTag | null {
  let best: TrailTag | null = null;
  let most = 0;
  for (const tag of TRAIL_TAGS) {
    const count = tags?.[tag] ?? 0;
    if (count > most) {
      best = tag;
      most = count;
    }
  }
  return best;
}

/**
 * The reasons of an area with their strings, most first inside each group, and the groups
 * in the order items, customer, quantity. A reason without a string is left out.
 */
export function topReasons(counts: Record<string, number> | null | undefined): TrailReasonRow[] {
  const group = (reason: string) =>
    reason.startsWith("item_") ? 0 : reason.startsWith("customer_") ? 1 : 2;
  return TRAIL_REASONS.filter((reason) => (counts?.[reason] ?? 0) > 0)
    .map((reason) => ({ reason, strings: counts?.[reason] ?? 0 }))
    .sort(
      (a, b) =>
        group(a.reason) - group(b.reason) ||
        b.strings - a.strings ||
        TRAIL_REASONS.indexOf(a.reason) - TRAIL_REASONS.indexOf(b.reason),
    );
}

/** The touch points up to the quote sent (inclusive) and after it; all before without one. */
export function splitTouches(string: TrailString): TrailTouchSplit {
  const touches = string.touches ?? [];
  const cut = string.quote_sent_at ? Date.parse(string.quote_sent_at) : Number.NaN;
  if (Number.isNaN(cut)) {
    return { before: [...touches], after: [] };
  }
  return {
    before: touches.filter((touch) => Date.parse(touch.at) <= cut),
    after: touches.filter((touch) => Date.parse(touch.at) > cut),
  };
}

/** "100200 - Firm Example", the account alone, or "No customer". */
export function accountText(account: TrailAccount | null | undefined): string {
  if (!account?.account) {
    return TRAIL_LABELS.noCustomer;
  }
  return account.name ? `${account.account} - ${account.name}` : account.account;
}

/** The quoted lines with the same item (the quantity or the price may differ). */
export function partsFound(string: TrailString): number {
  return (string.quoted ?? []).filter((line) =>
    ["same_item", "qty_differs", "price_differs"].includes(line.verdict ?? "missed"),
  ).length;
}

/** The string as CSV: one line per string, with a header line. */
export function trailCsv(strings: readonly TrailString[]): string {
  const header = [
    "area",
    "quotation",
    "sales_order",
    "customer_po",
    "mails",
    "i2q_customer",
    "quoted_customer",
    "customer_match",
    "customer_check",
    "quoted_lines",
    "parts_found",
    "ordered_lines",
    "ordered_found",
    "fully_right",
    "tags",
  ];
  const lines = strings.map((string) =>
    [
      string.area,
      string.quotation,
      string.sales_order ?? "",
      string.customer_po ?? "",
      String(string.mails?.length ?? 0),
      string.i2q_customer?.account ?? "",
      string.quoted_customer?.account ?? "",
      string.customer_match ?? "",
      string.customer_check?.verdict ?? "",
      String(string.quoted?.length ?? 0),
      String(partsFound(string)),
      String(string.order_lines_total ?? 0),
      String(string.order_lines_found ?? 0),
      string.fully_right ? "yes" : "no",
      (string.tags ?? []).join(" "),
    ]
      .map(csvCell)
      .join(","),
  );
  return [header.join(","), ...lines].join("\n");
}

/** One CSV cell, quoted when it holds a comma, a quote or a line break. */
function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}
